import type { TierHero } from '../types';
import { heroDisplayName } from './heroDisplayName';

export interface ScatterLayout {
  plotWidth: number;
  plotHeight: number;
}

export interface ScatterPoint {
  cx: number;
  cy: number;
  tier: TierHero['tier'];
  heroId: number;
  heroName: string;
  headUrl: string | null;
  /** ピック率(%) + BAN率(%) — TierTooltip の内訳と同じ換算 */
  banPickPercent: number;
  winPercent: number;
  tierScore: number;
}

/** バンピック率（%）= ツールチップのピック表示 + BAN表示 */
export function banPickRatePercent(pickRate: number, banRate: number): number {
  return pickRate * 1000 + banRate * 100;
}

/** Tooltip と同じ勝率表示（%） */
export function winRatePercent(winRate: number): number {
  return winRate * 100;
}

/** 対数プロット用の下限（%）。これ未満はこの値として扱い log(0) を避ける */
export const BAN_PICK_LOG_FLOOR_PERCENT = 0.05;

/** 横軸の対数スケール用座標（log10） */
export function banPickAxisLog10(percent: number): number {
  return Math.log10(Math.max(percent, BAN_PICK_LOG_FLOOR_PERCENT));
}

function clamp01(t: number): number {
  return Math.min(1, Math.max(0, t));
}

export interface ScatterAxisScales {
  minLogX: number;
  maxLogX: number;
  sameLogX: boolean;
  spanLogX: number;
  minY: number;
  maxY: number;
  sameY: boolean;
  spanY: number;
  banPickMin: number;
  banPickMax: number;
}

export function computeScatterAxisScales(tierHeroes: TierHero[]): ScatterAxisScales | null {
  if (tierHeroes.length === 0) return null;
  const banPickPercents = tierHeroes.map((th) =>
    banPickRatePercent(th.hero.pick_rate, th.hero.ban_rate)
  );
  const logXs = banPickPercents.map(banPickAxisLog10);
  const ys = tierHeroes.map((th) => winRatePercent(th.hero.win_rate));
  const minLogX = Math.min(...logXs);
  const maxLogX = Math.max(...logXs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const sameLogX = minLogX === maxLogX;
  const sameY = minY === maxY;
  return {
    minLogX,
    maxLogX,
    sameLogX,
    spanLogX: sameLogX ? 1 : maxLogX - minLogX,
    minY,
    maxY,
    sameY,
    spanY: sameY ? 1 : maxY - minY,
    banPickMin: Math.min(...banPickPercents),
    banPickMax: Math.max(...banPickPercents),
  };
}

export function buildScatterPoints(
  tierHeroes: TierHero[],
  layout: ScatterLayout
): ScatterPoint[] {
  const scales = computeScatterAxisScales(tierHeroes);
  if (!scales) return [];

  const banPickPercents = tierHeroes.map((th) =>
    banPickRatePercent(th.hero.pick_rate, th.hero.ban_rate)
  );
  const logXs = banPickPercents.map(banPickAxisLog10);
  const ys = tierHeroes.map((th) => winRatePercent(th.hero.win_rate));

  return tierHeroes.map((th, i) => {
    const banPickPercent = banPickPercents[i];
    const winPercent = ys[i];
    const nx = scales.sameLogX ? 0.5 : clamp01((logXs[i]! - scales.minLogX) / scales.spanLogX);
    const ny = scales.sameY ? 0.5 : clamp01((winPercent - scales.minY) / scales.spanY);
    const cx = nx * layout.plotWidth;
    const cy = (1 - ny) * layout.plotHeight;
    return {
      cx,
      cy,
      tier: th.tier,
      heroId: th.hero.hero_id,
      heroName: heroDisplayName(th.hero.hero_id, th.hero.hero_name),
      headUrl: th.hero.head_url,
      banPickPercent,
      winPercent,
      tierScore: th.tierScore,
    };
  });
}
